import React, { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import { ShareStrip, StatRow, StatTile } from '@/components/admin/StatTile';
import { LegendItem, Segmented } from '@/components/ds';
import { Bars, Donut, Legend, Spark, lineSeries, quietAxis, quietGrid } from '@/components/viz';
import { useAdminStats } from '@/hooks/admin/useAdminStats';
import { useAdminUsers } from '@/hooks/admin/useAdminUsers';
import { AdminStatsAdvisor } from '@/services/api/admin';

// ─── Design tokens ─────────────────────────────────────────────────────────
const C = {
  card: 'var(--tx-glass-panel)',
  border: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  blue: '#5AE9FF',
  amber: '#FFF981',
  coral: '#FF9A4D',
  red: '#DB5282',
  green: '#7AE3A0',
  purple: '#B39DDB',
  monitor: '#9A938A',
};
const font = "'Manrope', sans-serif";

const DEAL_STATUS_COLORS: Record<string, string> = {
  Active: C.lime,
  'Under Contract': C.blue,
  Pending: C.amber,
  Closed: C.green,
  Lost: C.red,
};
const STATUS_ORDER = ['Active', 'Under Contract', 'Pending', 'Closed', 'Lost'];

const TABS = ['Pulse', 'Deal Flow', 'Advisors', 'Client Engagement', 'Operations'] as const;
type Tab = (typeof TABS)[number];
// what each tab reads as, in sentence case
const TAB_LABELS: Record<Tab, string> = {
  Pulse: 'Pulse',
  'Deal Flow': 'Deal flow',
  Advisors: 'Advisors',
  'Client Engagement': 'Client engagement',
  Operations: 'Operations',
};

// ─── Formatters ────────────────────────────────────────────────────────────
function fmtMs(ms: number | null): string {
  if (ms === null) return '—';
  const s = ms / 1000;
  if (s < 60) return `${Math.round(s)}s`;
  const m = s / 60;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

function fmtRelative(ts: number | null): string {
  if (!ts) return '—';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtPct(ratio: number | null, decimals = 0): string {
  if (ratio === null) return '—';
  return `${(ratio * 100).toFixed(decimals)}%`;
}

function fmtDays(days: number | null): string {
  if (days === null) return '—';
  return `${Math.round(days)}d`;
}

function fmtMonth(m: string): string {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1).toLocaleString('default', { month: 'short' });
}

function responseColor(ms: number | null): string {
  if (ms === null) return C.subtle;
  if (ms < 3_600_000) return C.green; // < 1h
  if (ms < 86_400_000) return C.amber; // < 24h
  return C.red;
}

// Deal counts by status, in pipeline order, unknown statuses last
function statusEntries(byStatus: Record<string, number> | undefined) {
  if (!byStatus) return [];
  return STATUS_ORDER.filter((s) => (byStatus[s] ?? 0) > 0)
    .map((s) => ({ status: s, count: byStatus[s] ?? 0, color: DEAL_STATUS_COLORS[s] ?? C.muted }))
    .concat(
      Object.entries(byStatus)
        .filter(([s]) => !STATUS_ORDER.includes(s))
        .map(([s, count]) => ({ status: s, count: count as number, color: C.muted })),
    );
}

// ─── Hairline visuals ──────────────────────────────────────────────────────
type Seg = { n: number; color: string; label: string };

// Strip: shares of a whole as segments on the app's share strip (the tiles' ShareStrip), so rows and tiles read alike
const Strip: React.FC<{ segs: Seg[]; of: number }> = ({ segs, of }) => (
  <ShareStrip parts={segs.map((s) => ({ label: s.label, color: s.color, value: s.n }))} total={of} />
);

// one thin neutral bar per item on a baseline (the viz system's Bars), each bar 6px wide so a handful never turns into
// blocks. For a spread across people, not a series over time
const MiniBars: React.FC<{ data: { label: string; value: number }[]; format?: (v: number) => string }> = ({
  data,
  format,
}) =>
  data.length === 0 ? (
    <div style={{ height: 2, borderRadius: 1, background: C.border }} />
  ) : (
    <div style={{ width: `min(100%, ${data.length * 9}px)` }}>
      <Bars data={data} height={20} format={format} />
    </div>
  );

// a short series over time as the viz system's Spark (1.5px line, the latest point in lime); months with no value are
// left out of the line rather than drawn as zero
const TrendSpark: React.FC<{ values: (number | null)[] }> = ({ values }) => {
  const known = values.filter((v): v is number => v !== null);
  return known.length < 2 ? (
    <div style={{ height: 2, borderRadius: 1, background: C.border }} />
  ) : (
    <Spark data={known} height={20} />
  );
};

// Recharts: mark only the series' latest point, in lime (the current period); every other point stays undotted
const latestDot =
  (lastIndex: number) =>
  (props: any): React.ReactElement => {
    const { cx, cy, index, value } = props;
    if (index !== lastIndex || value == null || cx == null || cy == null) return <g key={`d-${index}`} />;
    return <circle key={`d-${index}`} cx={cx} cy={cy} r={3.5} fill={C.lime} stroke="none" />;
  };

// the legend tick: 2px × 12px in the data colour
const TickMark: React.FC<{ color: string }> = ({ color }) => (
  <span
    style={{
      width: 2,
      height: 12,
      borderRadius: 1,
      background: color,
      flexShrink: 0,
      display: 'inline-block',
    }}
  />
);

// ─── Deals donut ───────────────────────────────────────────────────────────
// the stage mix as the viz system's Donut (2px-cornered slices, hover names a slice and dims the rest) with its Legend
const DealsDonut: React.FC<{ entries: { status: string; count: number; color: string }[] }> = ({
  entries,
}) => {
  const parts = entries.map((e) => ({ key: e.status, label: e.status, value: e.count, color: e.color }));
  const total = entries.reduce((s, e) => s + e.count, 0);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        flexWrap: 'wrap',
      }}>
      <div style={{ width: 200, flexShrink: 0 }}>
        <Donut parts={parts} size={200} label={total} sub="Total" />
      </div>
      <div style={{ minWidth: 140 }}>
        <Legend parts={parts} />
      </div>
    </div>
  );
};

// ─── Shared components ─────────────────────────────────────────────────────
const sheetStyle: React.CSSProperties = {
  background: C.card,
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow)',
  borderRadius: 24,
  padding: 20,
  fontFamily: font,
  minWidth: 0,
};

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => <div style={{ ...sheetStyle, ...style }}>{children}</div>;

// a sheet's header: the title in ink, a short meta caption on the right
const SectionTitle: React.FC<{ children: React.ReactNode; meta?: React.ReactNode }> = ({
  children,
  meta,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 10,
      flexWrap: 'wrap',
      marginBottom: 16,
    }}>
    <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text }}>
      {children}
    </span>
    {meta && <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>{meta}</span>}
  </div>
);

// the explanatory line under a sheet's title
const SectionNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.5,
      color: C.muted,
      marginTop: -8,
      marginBottom: 16,
    }}>
    {children}
  </div>
);

const EmptyNote: React.FC<{ children: React.ReactNode; height?: number }> = ({
  children,
  height,
}) => (
  <div
    style={{
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: C.subtle,
      fontSize: 13,
      fontWeight: 500,
    }}>
    {children}
  </div>
);

// a ratio (0–1) as its share of the tile's strip
const RatioShare: React.FC<{ ratio: number | null | undefined; color: string; label: string }> = ({
  ratio,
  color,
  label,
}) => (
  <ShareStrip
    parts={[{ label, color, value: ratio ?? 0 }]}
    total={1}
    format={(v) => fmtPct(v)}
  />
);

// A figure inside a sheet, value first and its label beneath: parted from its neighbours by a hairline above, never boxed
const MiniStat: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  tick?: string;
  visual?: React.ReactNode;
}> = ({ label, value, sub, tick, visual }) => (
  <div
    style={{
      borderTop: `1px solid ${C.border}`,
      paddingTop: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      minWidth: 0,
    }}>
    <span
      style={{
        fontSize: 28,
        fontWeight: 200,
        letterSpacing: '-0.05em',
        lineHeight: 1,
        color: C.text,
        fontVariantNumeric: 'tabular-nums',
      }}>
      {value}
    </span>
    <span
      style={{
        marginTop: -2,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 500,
        color: C.muted,
      }}>
      {tick && <TickMark color={tick} />}
      {label}
    </span>
    {sub && <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>{sub}</span>}
    {visual}
  </div>
);

const Skeleton: React.FC<{ height?: number; radius?: number }> = ({ height = 18, radius = 6 }) => (
  <div
    style={{
      width: '100%',
      height,
      borderRadius: radius,
      background: 'var(--tx-fill)',
      animation: 'pulse 1.5s ease-in-out infinite',
    }}
  />
);

// two sheets side by side when there is room, stacked when not
const pairGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
  gap: 20,
  marginBottom: 20,
};

// a progress row: label and figure, a hairline of the share under them
const MeterRow: React.FC<{
  label: string;
  pct: number | null;
  color: string;
  trailing?: React.ReactNode;
  suffix?: string;
}> = ({ label, pct, color, trailing, suffix = '' }) => (
  <div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 10,
        marginBottom: 8,
      }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, whiteSpace: 'nowrap' }}>
        <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {pct !== null ? `${pct}%` : '—'}
        </span>
        {suffix}
        {trailing}
      </span>
    </div>
    <RatioShare ratio={pct !== null ? pct / 100 : 0} color={color} label={label} />
  </div>
);

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  unit?: string;
}> = ({ active, payload, label, unit = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: C.card,
        backgroundImage: 'var(--tx-surface-light)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: 'var(--tx-shadow-sm)',
        borderRadius: 12,
        padding: '10px 14px',
        fontFamily: font,
        fontSize: 12,
        minWidth: 140,
      }}>
      <div style={{ color: C.text, marginBottom: 8, fontWeight: 600 }}>{label}</div>
      {payload.map((p, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            alignItems: 'center',
            marginTop: 4,
          }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: C.muted,
              fontWeight: 500,
            }}>
            <TickMark color={p.color} />
            {p.name}
          </span>
          <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {p.value}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Tab content ───────────────────────────────────────────────────────────

type Alert = { label: string; value: number; sub: string; color: string; share: number; of: number };

const PulseTab: React.FC<{ stats: any; users: any[]; isLoading: boolean }> = ({
  stats,
  users,
  isLoading,
}) => {
  const advisors = users.filter((u) => u.platformRole === 'Advisor' && u.enabled);
  const buyers = users.filter((u) => u.platformRole === 'Buyer' && u.enabled);
  const company = stats?.company;
  const totalGroups = company?.totalGroups ?? (company?.activeGroups ?? 0) + (company?.archivedGroups ?? 0);
  const dealMix = statusEntries(company?.dealsByStatus).map((e) => ({
    label: e.status,
    color: e.color,
    value: e.count,
  }));

  const alerts: Alert[] =
    !isLoading && stats
      ? (
          [
            stats.company.stalledDeals > 0 && {
              label: 'Stalled deals',
              value: stats.company.stalledDeals,
              sub: '14+ days inactive',
              color: C.amber,
              share: stats.company.stalledDeals,
              of: stats.company.totalDeals,
            },
            stats.company.groupsWithNoDeals > 0 && {
              label: 'Unactivated groups',
              value: stats.company.groupsWithNoDeals,
              sub: 'No deal started',
              color: C.coral,
              share: stats.company.groupsWithNoDeals,
              of: totalGroups,
            },
            stats.propertyRequests.pending > 0 && {
              label: 'Pending requests',
              value: stats.propertyRequests.pending,
              sub: 'Awaiting approval',
              color: C.blue,
              share: stats.propertyRequests.pending,
              of: stats.propertyRequests.total,
            },
            stats.company.silentChurnCount > 0 && {
              label: 'Silent churn',
              value: stats.company.silentChurnCount,
              sub: 'Archived, never closed',
              color: C.red,
              share: stats.company.silentChurnCount,
              of: stats.company.archivedGroups,
            },
          ] as (Alert | false)[]
        ).filter((a): a is Alert => Boolean(a))
      : [];

  const growth: { month: string; groups: number; deals: number }[] =
    company?.growthByMonth.map((m: any) => ({
      ...m,
      month: fmtMonth(m.month),
    })) ?? [];
  const growthGroups = growth.reduce((s, m) => s + (m.groups ?? 0), 0);
  const growthDeals = growth.reduce((s, m) => s + (m.deals ?? 0), 0);

  return (
    <div className="kpi-tab">
      <StatRow columns={6}>
        <StatTile
          loading={isLoading}
          value={advisors.length}
          label="Active advisors"
          caption="Enabled"
          visual={
            <ShareStrip
              parts={[{ label: 'Advisors', color: C.coral, value: advisors.length }]}
              total={users.length}
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={buyers.length}
          label="Clients / buyers"
          caption="Enabled"
          visual={
            <ShareStrip
              parts={[{ label: 'Buyers', color: C.blue, value: buyers.length }]}
              total={users.length}
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={company?.activeGroups ?? 0}
          label="Active groups"
          caption={`${company?.archivedGroups ?? 0} archived`}
          visual={
            <ShareStrip
              parts={[
                { label: 'Active', color: C.lime, value: company?.activeGroups ?? 0 },
                { label: 'Archived', color: C.monitor, value: company?.archivedGroups ?? 0 },
              ]}
              total={totalGroups}
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={company?.totalDeals ?? 0}
          label="Total deals"
          caption="All advisors"
          visual={<ShareStrip parts={dealMix} total={company?.totalDeals ?? 0} />}
        />
        <StatTile
          loading={isLoading}
          value={company?.closedDeals30d ?? 0}
          label="Closed (30d)"
          caption={`${company?.closedDeals90d ?? 0} in 90d`}
          visual={
            <ShareStrip
              parts={[{ label: 'Closed, 30d', color: C.green, value: company?.closedDeals30d ?? 0 }]}
              total={company?.closedDeals90d ?? 0}
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={fmtPct(company?.certificationRate ?? null)}
          label="Certification rate"
          caption="All tasks completed"
          visual={
            <RatioShare ratio={company?.certificationRate} color={C.lime} label="Certified" />
          }
        />
      </StatRow>

      {alerts.length > 0 && (
        <>
          <SectionTitle meta={`${alerts.length} ${alerts.length === 1 ? 'signal' : 'signals'}`}>
            Needs attention
          </SectionTitle>
          <StatRow columns={alerts.length}>
            {alerts.map((a) => (
              <StatTile
                key={a.label}
                value={a.value}
                label={a.label}
                caption={a.sub}
                visual={
                  <ShareStrip
                    parts={[{ label: a.label, color: a.color, value: a.share }]}
                    total={a.of ?? 0}
                  />
                }
              />
            ))}
          </StatRow>
        </>
      )}

      <Card>
        <SectionTitle
          meta={
            isLoading ? undefined : `Last 12 months · ${growthGroups} groups · ${growthDeals} deals`
          }>
          Platform growth
        </SectionTitle>
        {isLoading ? (
          <Skeleton height={240} radius={12} />
        ) : growthGroups + growthDeals === 0 ? (
          <EmptyNote height={240}>No new groups or deals in the last 12 months.</EmptyNote>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <LegendItem color={C.text} label="New groups" count={growthGroups} />
              <LegendItem color={C.blue} label="New deals" count={growthDeals} />
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={growth} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid {...quietGrid} />
                <XAxis dataKey="month" {...quietAxis} />
                <YAxis {...quietAxis} width={28} allowDecimals={false} domain={[0, 'auto']} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: C.border, strokeWidth: 1 }} />
                <Line
                  type="monotone"
                  dataKey="groups"
                  {...lineSeries(C.text)}
                  strokeOpacity={0.8}
                  dot={latestDot(growth.length - 1)}
                  name="New groups"
                />
                <Line
                  type="monotone"
                  dataKey="deals"
                  {...lineSeries(C.blue)}
                  dot={latestDot(growth.length - 1)}
                  name="New deals"
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </Card>
    </div>
  );
};

const DealFlowTab: React.FC<{ stats: any; isLoading: boolean }> = ({ stats, isLoading }) => {
  const dealStatusEntries = React.useMemo(
    () => statusEntries(stats?.company.dealsByStatus),
    [stats],
  );

  const velocityData: { month: string; days: number | null }[] =
    stats?.pipeline.velocityTrend.map((v: any) => ({
      month: fmtMonth(v.month),
      days: v.avgDays !== null ? Math.round(v.avgDays) : null,
    })) ?? [];
  const hasVelocity = velocityData.some((v) => v.days !== null);

  const ageByStatus = Object.entries(stats?.pipeline.avgDealAgeDaysByStatus ?? {}) as [
    string,
    number,
  ][];

  return (
    <div className="kpi-tab">
      <StatRow columns={4}>
        <StatTile
          loading={isLoading}
          value={stats?.pipeline.dealsCreatedLast30d ?? 0}
          label="New deals (30d)"
          caption="New engagements"
          visual={
            <ShareStrip
              parts={[
                { label: 'New, 30d', color: C.blue, value: stats?.pipeline.dealsCreatedLast30d ?? 0 },
              ]}
              total={stats?.company.totalDeals ?? 0}
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={`${stats?.pipeline.dealsClosedLast30d ?? 0} / ${
            stats?.pipeline.dealsClosedLast90d ?? 0
          }`}
          label="Closed (30d / 90d)"
          caption="Rolling windows"
          visual={
            <ShareStrip
              parts={[
                { label: 'Closed, 30d', color: C.green, value: stats?.pipeline.dealsClosedLast30d ?? 0 },
              ]}
              total={stats?.pipeline.dealsClosedLast90d ?? 0}
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={fmtDays(stats?.pipeline.avgDealVelocityDays ?? null)}
          label="Avg close time"
          caption="Closed deals"
          visual={<TrendSpark values={velocityData.map((v) => v.days)} />}
        />
        <StatTile
          loading={isLoading}
          value={fmtPct(stats?.pipeline.avgOfferUtilization ?? null)}
          label="Avg offer utilization"
          caption="Active deals"
          visual={
            <RatioShare
              ratio={stats?.pipeline.avgOfferUtilization}
              color={C.amber}
              label="Utilization"
            />
          }
        />
      </StatRow>

      <div style={pairGrid}>
        <Card>
          <SectionTitle>Deals by stage</SectionTitle>
          {isLoading ? (
            <Skeleton height={200} radius={12} />
          ) : dealStatusEntries.length === 0 ? (
            <EmptyNote height={200}>No deals yet.</EmptyNote>
          ) : (
            <DealsDonut entries={dealStatusEntries} />
          )}
        </Card>

        <Card>
          <SectionTitle meta="Days">Avg deal age by stage</SectionTitle>
          <SectionNote>How long active deals have been sitting at each stage.</SectionNote>
          {isLoading ? (
            <Skeleton height={200} radius={12} />
          ) : ageByStatus.length === 0 ? (
            <EmptyNote height={200}>No active deals.</EmptyNote>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={ageByStatus.map(([status, days]) => ({
                  status,
                  days: Math.round(days),
                  fill: DEAL_STATUS_COLORS[status] ?? C.muted,
                }))}
                barSize={12}
                margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid {...quietGrid} />
                <XAxis dataKey="status" {...quietAxis} />
                <YAxis {...quietAxis} width={28} allowDecimals={false} />
                <Tooltip
                  content={<ChartTooltip unit="d" />}
                  cursor={{ fill: 'var(--tx-fill)' }}
                />
                <Bar dataKey="days" radius={[2, 2, 0, 0]} name="Avg age" isAnimationActive={false}>
                  {ageByStatus.map(([status], i) => (
                    <Cell key={i} fill={DEAL_STATUS_COLORS[status] ?? C.muted} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card>
        <SectionTitle meta="Last 6 months · avg days to close">Deal close velocity</SectionTitle>
        {isLoading ? (
          <Skeleton height={180} radius={12} />
        ) : !hasVelocity ? (
          <EmptyNote height={180}>No deals closed in the last 6 months.</EmptyNote>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={velocityData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid {...quietGrid} />
              <XAxis dataKey="month" {...quietAxis} />
              <YAxis {...quietAxis} width={32} allowDecimals={false} />
              <Tooltip
                content={<ChartTooltip unit="d" />}
                cursor={{ stroke: C.border, strokeWidth: 1 }}
              />
              <Line
                type="monotone"
                dataKey="days"
                {...lineSeries(C.green)}
                dot={latestDot(velocityData.length - 1)}
                name="Avg days to close"
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
};

// a count in ink with its tick, or a quiet placeholder when there is none
const CountCell: React.FC<{ n: number; color: string; zero?: string }> = ({
  n,
  color,
  zero = '—',
}) =>
  n > 0 ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <TickMark color={color} />
      <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {n}
      </span>
    </span>
  ) : (
    <span style={{ color: C.subtle }}>{zero}</span>
  );

const AdvisorsTab: React.FC<{
  stats: any;
  advisorNameMap: Map<string, string>;
  isLoading: boolean;
}> = ({ stats, advisorNameMap, isLoading }) => {
  const rows = React.useMemo((): (AdminStatsAdvisor & { name: string })[] => {
    if (!stats) return [];
    return [...stats.advisors]
      .sort((a, b) => b.activeGroups - a.activeGroups)
      .map((a: AdminStatsAdvisor) => ({
        ...a,
        name: advisorNameMap.get(a.advisorId) ?? a.advisorId,
      }));
  }, [stats, advisorNameMap]);

  const cols = [
    { label: 'Advisor', w: 'minmax(140px, 1fr)' },
    { label: 'Groups', w: '60px' },
    { label: 'Deals', w: '60px' },
    { label: 'Closed', w: '60px' },
    { label: 'Stalled', w: '65px' },
    { label: 'Fee ↓', w: '60px' },
    { label: 'Response', w: '80px' },
    { label: 'Early / late', w: '120px' },
    { label: 'Last active', w: '90px' },
  ];
  const grid = cols.map((c) => c.w).join(' ');
  const firstDealDays = rows
    .filter((a) => a.avgDaysToFirstDeal !== null)
    .map((a) => ({ label: a.name, value: Math.round(a.avgDaysToFirstDeal ?? 0) }));
  const feeRate: number | null = stats?.company.feeOverrideRate ?? null;

  return (
    <div className="kpi-tab">
      <StatRow columns={3}>
        <StatTile
          loading={isLoading}
          value={fmtPct(stats?.company.coAdvisorCollabRate ?? null)}
          label="Co-advisor collab rate"
          caption="With co-advisors"
          visual={
            <RatioShare
              ratio={stats?.company.coAdvisorCollabRate}
              color={C.purple}
              label="With co-advisors"
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={fmtPct(feeRate)}
          label="Fee override rate"
          caption="Discounted fees"
          visual={
            <RatioShare
              ratio={feeRate}
              color={(feeRate ?? 0) > 0.2 ? C.red : C.monitor}
              label="Discounted"
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={
            rows.length > 0
              ? fmtDays(
                  rows.reduce((s, a) => s + (a.avgDaysToFirstDeal ?? 0), 0) /
                    rows.filter((a) => a.avgDaysToFirstDeal !== null).length || null,
                )
              : '—'
          }
          label="Avg days to first deal"
          caption="Group → first deal"
          visual={<MiniBars data={firstDealDays} format={(v) => `${v}d`} />}
        />
      </StatRow>

      <Card>
        <SectionTitle meta={isLoading ? undefined : `${rows.length} advisors`}>
          Advisor performance
        </SectionTitle>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={44} radius={8} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyNote height={120}>No advisor data yet.</EmptyNote>
        ) : (
          <div style={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
            <div style={{ minWidth: 820 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: grid,
                  gap: 12,
                  padding: '0 0 10px',
                  borderBottom: `1px solid ${C.border}`,
                }}>
                {cols.map((c) => (
                  <div key={c.label} style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>
                    {c.label}
                  </div>
                ))}
              </div>
              {rows.map((a, i) => {
                const closed = a.dealsByStatus.Closed ?? 0;
                const decaySignal =
                  a.avgResponseMsEarly !== null &&
                  a.avgResponseMsLate !== null &&
                  a.avgResponseMsLate > a.avgResponseMsEarly * 2;
                return (
                  <div
                    key={a.advisorId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: grid,
                      gap: 12,
                      padding: '12px 0',
                      borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${C.border}`,
                      alignItems: 'center',
                      fontSize: 13,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: C.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {a.name}
                    </div>
                    <div style={{ color: C.text }}>{a.activeGroups}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                      <span style={{ color: C.text }}>{a.totalDeals}</span>
                      {a.totalDeals > 0 && (
                        <ShareStrip
                          parts={statusEntries(a.dealsByStatus).map((e) => ({
                            label: e.status,
                            color: e.color,
                            value: e.count,
                          }))}
                          total={a.totalDeals}
                        />
                      )}
                    </div>
                    <div>
                      <CountCell n={closed} color={C.green} zero="0" />
                    </div>
                    <div>
                      <CountCell n={a.stalledDeals} color={C.amber} />
                    </div>
                    <div>
                      <CountCell n={a.feeOverrideCount} color={C.coral} />
                    </div>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontWeight: 600,
                        color: a.avgResponseMs === null ? C.subtle : C.text,
                      }}>
                      {a.avgResponseMs !== null && (
                        <TickMark color={responseColor(a.avgResponseMs)} />
                      )}
                      {fmtMs(a.avgResponseMs)}
                    </div>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 500,
                        color: decaySignal ? C.text : C.muted,
                      }}>
                      {decaySignal && <TickMark color={C.red} />}
                      {fmtMs(a.avgResponseMsEarly)} / {fmtMs(a.avgResponseMsLate)}
                      {decaySignal && <span style={{ fontSize: 11, color: C.red }}>↑</span>}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>
                      {fmtRelative(a.lastActivityAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const ClientEngagementTab: React.FC<{
  stats: any;
  advisorNameMap: Map<string, string>;
  isLoading: boolean;
}> = ({ stats, advisorNameMap, isLoading }) => {
  const rows: AdminStatsAdvisor[] = stats?.advisors ?? [];
  const initiationRows = rows
    .filter((a) => a.avgInitiationRatio !== null)
    .sort((a, b) => (b.avgInitiationRatio ?? 0) - (a.avgInitiationRatio ?? 0));
  const decayData = rows
    .filter((a) => a.avgResponseMsEarly !== null || a.avgResponseMsLate !== null)
    .map((a) => ({
      advisor: (advisorNameMap.get(a.advisorId) ?? a.advisorId).split(' ')[0],
      early: a.avgResponseMsEarly !== null ? Math.round(a.avgResponseMsEarly / 60000) : 0,
      late: a.avgResponseMsLate !== null ? Math.round(a.avgResponseMsLate / 60000) : 0,
    }));
  const firstDealDays = rows
    .filter((a) => a.avgDaysToFirstDeal !== null)
    .map((a) => ({
      label: advisorNameMap.get(a.advisorId) ?? a.advisorId,
      value: Math.round(a.avgDaysToFirstDeal ?? 0),
    }));

  return (
    <div className="kpi-tab">
      <StatRow columns={4}>
        <StatTile
          loading={isLoading}
          value={fmtPct(stats?.tasks.certificationRate ?? null)}
          label="Certification rate"
          caption="All tasks complete"
          visual={
            <RatioShare ratio={stats?.tasks.certificationRate} color={C.lime} label="Certified" />
          }
        />
        <StatTile
          loading={isLoading}
          value={fmtPct(stats?.tasks.clientCompletionRate ?? null)}
          label="Client task completion"
          caption="Buyer tasks"
          visual={
            <RatioShare
              ratio={stats?.tasks.clientCompletionRate}
              color={C.blue}
              label="Completed"
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={stats?.company.silentChurnCount ?? 0}
          label="Silent churn"
          caption={`${fmtPct(stats?.company.silentChurnRate ?? null)} of archived`}
          visual={
            <RatioShare
              ratio={stats?.company.silentChurnRate}
              color={C.red}
              label="Of archived"
            />
          }
        />
        <StatTile
          loading={isLoading}
          value={fmtDays(
            stats?.advisors.length > 0
              ? stats.advisors.reduce((s: number, a: any) => s + (a.avgDaysToFirstDeal ?? 0), 0) /
                  stats.advisors.filter((a: any) => a.avgDaysToFirstDeal !== null).length || null
              : null,
          )}
          label="Avg days to first deal"
          caption="Group → first deal"
          visual={<MiniBars data={firstDealDays} format={(v) => `${v}d`} />}
        />
      </StatRow>

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle meta="Advisor-initiated share">Conversation initiation</SectionTitle>
        <SectionNote>
          Who opens the dialogue? Ratio of advisor-initiated conversation sessions per group. High =
          advisor is always reaching out; low = clients are engaged and self-directing.
        </SectionNote>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={32} radius={8} />
            ))}
          </div>
        ) : initiationRows.length === 0 ? (
          <EmptyNote height={80}>No messaging data yet.</EmptyNote>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {initiationRows.map((a) => {
              const ratio = a.avgInitiationRatio ?? 0;
              const pct = Math.round(ratio * 100);
              const color = ratio > 0.8 ? C.amber : ratio > 0.5 ? C.blue : C.green;
              return (
                <MeterRow
                  key={a.advisorId}
                  label={advisorNameMap.get(a.advisorId) ?? a.advisorId}
                  pct={pct}
                  color={color}
                  suffix=" advisor-initiated"
                />
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle meta="Minutes">Response time decay</SectionTitle>
        <SectionNote>
          Early vs. late relationship: avg response time in the first 30 days of a client
          relationship vs. after. A large gap signals attention drift.
        </SectionNote>
        {isLoading ? (
          <Skeleton height={200} radius={12} />
        ) : decayData.length === 0 ? (
          <EmptyNote height={200}>No response-time data yet.</EmptyNote>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <LegendItem color={C.green} label="First 30 days" />
              <LegendItem color={C.amber} label="After 30 days" />
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={decayData}
                barCategoryGap="40%"
                barGap={2}
                margin={{ top: 10, right: 0, left: 0, bottom: 5 }}>
                <CartesianGrid {...quietGrid} />
                <XAxis dataKey="advisor" {...quietAxis} />
                <YAxis {...quietAxis} width={36} unit="m" />
                <Tooltip
                  content={<ChartTooltip unit="m" />}
                  cursor={{ fill: 'var(--tx-fill)' }}
                />
                <Bar
                  dataKey="early"
                  barSize={8}
                  fill={C.green}
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                  name="First 30 days"
                />
                <Bar
                  dataKey="late"
                  barSize={8}
                  fill={C.amber}
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                  name="After 30 days"
                />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </Card>
    </div>
  );
};

const OperationsTab: React.FC<{ stats: any; isLoading: boolean }> = ({ stats, isLoading }) => {
  const req = stats?.propertyRequests;
  const reqTotal: number = req?.total ?? 0;
  const approvalPct =
    req?.approvalRate !== null && req?.approvalRate !== undefined
      ? Math.round(req.approvalRate * 100)
      : null;

  return (
    <div className="kpi-tab">
      <div style={pairGrid}>
        <Card>
          <SectionTitle>Task completion</SectionTitle>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={60} radius={10} />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {[
                {
                  label: 'Overall completion',
                  value: stats?.tasks.completionRate,
                  total: stats?.tasks.total,
                  done: stats?.tasks.completed,
                },
                { label: 'Advisor tasks', value: stats?.tasks.advisorCompletionRate },
                { label: 'Client tasks', value: stats?.tasks.clientCompletionRate },
              ].map(({ label, value, total, done }) => {
                const pct = value !== null && value !== undefined ? Math.round(value * 100) : null;
                const color =
                  pct === null ? C.subtle : pct >= 70 ? C.green : pct >= 40 ? C.amber : C.red;
                return (
                  <MeterRow
                    key={label}
                    label={label}
                    pct={pct}
                    color={color}
                    trailing={
                      total !== undefined && (
                        <span style={{ marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}>
                          {done} / {total}
                        </span>
                      )
                    }
                  />
                );
              })}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 20,
                  marginTop: 4,
                }}>
                <MiniStat
                  label="Avg days to complete"
                  value={fmtDays(stats?.tasks.avgCompletionDays ?? null)}
                  sub="From creation"
                />
                <MiniStat
                  label="Certified deals"
                  value={stats?.tasks.certifiedDeals ?? 0}
                  sub="Signed off"
                  tick={C.lime}
                  visual={<RatioShare ratio={stats?.tasks.certificationRate} color={C.lime} label="Certified" />}
                />
              </div>
            </div>
          )}
        </Card>

        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionTitle>Property requests</SectionTitle>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={60} radius={10} />
              ))}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 18,
                flex: 1,
              }}>
              <MeterRow label="Approval rate" pct={approvalPct} color={C.green} />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 20,
                }}>
                <MiniStat
                  label="Total"
                  value={reqTotal}
                  visual={
                    <Strip
                      segs={[
                        { n: req?.approved ?? 0, color: C.green, label: 'Approved' },
                        { n: req?.pending ?? 0, color: C.amber, label: 'Pending' },
                        { n: req?.rejected ?? 0, color: C.red, label: 'Rejected' },
                      ]}
                      of={reqTotal}
                    />
                  }
                />
                <MiniStat
                  label="Pending"
                  value={req?.pending ?? 0}
                  tick={req?.pending > 0 ? C.amber : undefined}
                  visual={<Strip segs={[{ n: req?.pending ?? 0, color: C.amber, label: 'Pending' }]} of={reqTotal} />}
                />
                <MiniStat
                  label="Approved"
                  value={req?.approved ?? 0}
                  tick={C.green}
                  visual={
                    <Strip segs={[{ n: req?.approved ?? 0, color: C.green, label: 'Approved' }]} of={reqTotal} />
                  }
                />
                <MiniStat
                  label="Avg approval time"
                  value={fmtDays(req?.avgApprovalDays ?? null)}
                  sub="Request to resolution"
                />
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ─── Main ──────────────────────────────────────────────────────────────────
export const KpisTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Pulse');
  const { stats, isLoading: statsLoading } = useAdminStats();
  const { users, isLoading: usersLoading } = useAdminUsers();
  const isLoading = statsLoading || usersLoading;

  const advisorNameMap = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) {
      const name = u.profile
        ? [u.profile.firstName, u.profile.lastName].filter(Boolean).join(' ')
        : u.name ?? u.email ?? u.userId;
      m.set(u.userId, name || u.email || u.userId);
    }
    return m;
  }, [users]);

  return (
    <div
      style={{
        fontFamily: font,
        height: '100%',
        overflow: 'auto',
        padding: '20px 0',
        boxSizing: 'border-box',
        scrollbarWidth: 'none' as const,
        color: C.text,
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* a tab fills the view height: its last section grows to the frame's bottom gutter, so a short tab never ends
          in an empty band (a long one just scrolls) */}
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
        .kpi-tab { flex: 1 0 auto; display: flex; flex-direction: column; }
        .kpi-tab > :last-child { flex: 1 0 auto; margin-bottom: 0 !important; }`}</style>

      {/* Tab nav */}
      <div style={{ marginBottom: 20 }}>
        <Segmented<Tab>
          label="KPI section"
          value={activeTab}
          onChange={setActiveTab}
          options={TABS.map((t) => [t, TAB_LABELS[t]])}
        />
      </div>

      {activeTab === 'Pulse' && <PulseTab stats={stats} users={users} isLoading={isLoading} />}
      {activeTab === 'Deal Flow' && <DealFlowTab stats={stats} isLoading={isLoading} />}
      {activeTab === 'Advisors' && (
        <AdvisorsTab stats={stats} advisorNameMap={advisorNameMap} isLoading={isLoading} />
      )}
      {activeTab === 'Client Engagement' && (
        <ClientEngagementTab stats={stats} advisorNameMap={advisorNameMap} isLoading={isLoading} />
      )}
      {activeTab === 'Operations' && <OperationsTab stats={stats} isLoading={isLoading} />}
    </div>
  );
};
